import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';

const StoryPreview = () => {
  const { id: storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [options, setOptions] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch story details
  const fetchStory = useCallback(async () => {
    try {
      setLoading(true);
      const { data: storyData, error: storyError } = await supabase
        .from('stories_sb2847x9')
        .select('*')
        .eq('id', storyId)
        .single();

      if (storyError) throw storyError;
      setStory(storyData);

      // Look for a start node first
      const { data: startNodeData, error: startNodeError } = await supabase
        .from('story_nodes_sb2847x9')
        .select('*')
        .eq('story_id', storyId)
        .eq('is_start_node', true)
        .single();

      if (!startNodeError && startNodeData) {
        // Start node found
        loadNode(startNodeData.id);
      } else if (storyData.start_node_id) {
        // Try using start_node_id from story
        loadNode(storyData.start_node_id);
      } else {
        // Try to find any node to start with
        const { data: firstNode, error: nodeError } = await supabase
          .from('story_nodes_sb2847x9')
          .select('*')
          .eq('story_id', storyId)
          .limit(1)
          .single();

        if (nodeError) {
          setCurrentNode(null);
          setOptions([]);
          setLoading(false);
          return;
        }
        loadNode(firstNode.id);
      }
    } catch (err) {
      console.error('Error fetching story:', err);
      setError('Failed to load story. Please try again.');
      setLoading(false);
    }
  }, [storyId]);

  useEffect(() => {
    fetchStory();
  }, [fetchStory]);

  const loadNode = async (nodeId) => {
    try {
      setLoading(true);
      
      // Get node data
      const { data: nodeData, error: nodeError } = await supabase
        .from('story_nodes_sb2847x9')
        .select('*')
        .eq('id', nodeId)
        .single();

      if (nodeError) throw nodeError;
      setCurrentNode(nodeData);
      setHistory(prev => [...prev, nodeId]);

      // Get options for this node
      const { data: optionsData, error: optionsError } = await supabase
        .from('options_sb2847x9')
        .select('*')
        .eq('story_node_id', nodeId);

      if (optionsError) throw optionsError;
      setOptions(optionsData || []);
    } catch (err) {
      console.error('Error loading node:', err);
      setError('Failed to load story node. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionClick = (nextNodeId) => {
    loadNode(nextNodeId);
  };

  const handleGoBack = () => {
    if (history.length <= 1) return;
    
    const newHistory = [...history];
    newHistory.pop(); // Remove current node
    const previousNodeId = newHistory[newHistory.length - 1];
    
    // Remove the last item from history since loadNode will add it again
    setHistory(newHistory.slice(0, -1));
    loadNode(previousNodeId);
  };

  const handleBackToEditor = () => {
    navigate(`/story/${storyId}/edit`);
  };

  // Calculate the optimal layout for options buttons
  const getOptionButtonClasses = (optionsCount) => {
    // Base classes for all option buttons
    let baseClasses = "choice-button bg-pink-100 hover:bg-pink-200 text-pink-800 py-2 px-3 rounded-xl text-left font-fun font-medium transition-all duration-200";
    
    // For fewer options, make buttons larger
    if (optionsCount <= 2) {
      return `${baseClasses} text-base py-3 px-4`;
    } else if (optionsCount <= 4) {
      return `${baseClasses} text-sm`;
    } else {
      return `${baseClasses} text-xs`;
    }
  };

  // Determine if we need two rows for options
  const needsTwoRows = options.length > 4;
  
  // Split options into rows if needed
  const getOptionsRows = () => {
    if (!needsTwoRows) return [options];
    
    const midpoint = Math.ceil(options.length / 2);
    return [options.slice(0, midpoint), options.slice(midpoint)];
  };

  const optionsRows = getOptionsRows();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="ml-4 text-gray-600">Loading story preview...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="story-preview bg-white rounded-3xl shadow-dreamy p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
          onClick={handleBackToEditor}
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="text-lg" />
          Back to Editor
        </motion.button>
        
        <div className="flex items-center gap-3">
          {story?.story_image_url && (
            <img 
              src={story.story_image_url} 
              alt="Story thumbnail" 
              className="w-10 h-10 object-cover rounded-full border-2 border-purple-200" 
            />
          )}
          <h2 className="text-2xl font-display font-bold text-purple-700">
            {story?.title ? `Preview: ${story.title}` : 'Story Preview'}
          </h2>
        </div>
        
        <div className="w-[100px]"></div> {/* Spacer for alignment */}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
          <button
            className="mt-2 bg-red-200 hover:bg-red-300 text-red-800 px-3 py-1 rounded-md"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {!currentNode ? (
        <div className="text-center py-12">
          <div className="mb-4 text-purple-500">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-5xl mx-auto" />
          </div>
          <h3 className="text-xl font-display font-bold text-purple-700 mb-2">
            No Content Yet
          </h3>
          <p className="text-gray-600 mb-6">
            This story doesn't have any nodes yet or a start node isn't set.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2 mx-auto"
            onClick={handleBackToEditor}
          >
            <SafeIcon icon={FiIcons.FiEdit} className="text-lg" />
            Create Story Content
          </motion.button>
        </div>
      ) : (
        <>
          {/* Side-by-side layout for content and image */}
          <div className="flex flex-col md:flex-row gap-6 mb-6">
            {/* Story content - left side */}
            <div className="story-content bg-purple-50 rounded-2xl p-6 flex-1">
              <p className="text-lg font-fun leading-relaxed">{currentNode.content}</p>
              {currentNode.is_end_node && (
                <div className="mt-4 p-2 bg-red-100 text-red-800 rounded-lg inline-block">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiXCircle} />
                    <span className="font-medium">End of Story</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Image - right side */}
            {currentNode.image_url && (
              <div className="flex-1 rounded-xl overflow-hidden shadow-soft">
                <img 
                  src={currentNode.image_url} 
                  alt="Story scene" 
                  className="w-full h-full object-cover max-h-80" 
                />
              </div>
            )}
          </div>

          <div className="choices-container">
            {!currentNode.is_end_node && (
              <h3 className="text-lg font-display font-semibold text-purple-600 mb-3">
                {options.length > 0 ? 'What will you do?' : 'No choices available'}
              </h3>
            )}
            
            {/* First row of options */}
            {optionsRows[0] && optionsRows[0].length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3 justify-between">
                {optionsRows[0].map((option) => (
                  option.next_node_id ? (
                    <motion.button
                      key={option.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={getOptionButtonClasses(options.length)}
                      onClick={() => handleOptionClick(option.next_node_id)}
                      style={{ flexBasis: `calc(${100 / optionsRows[0].length}% - ${(optionsRows[0].length - 1) * 0.75}rem)` }}
                    >
                      {option.option_text}
                    </motion.button>
                  ) : (
                    <div
                      key={option.id}
                      className="bg-gray-100 text-gray-500 py-2 px-3 rounded-xl text-left font-fun font-medium opacity-70"
                      style={{ flexBasis: `calc(${100 / optionsRows[0].length}% - ${(optionsRows[0].length - 1) * 0.75}rem)` }}
                    >
                      {option.option_text}
                      <span className="text-xs italic">(No destination set)</span>
                    </div>
                  )
                ))}
              </div>
            )}
            
            {/* Second row of options if needed */}
            {needsTwoRows && optionsRows[1] && optionsRows[1].length > 0 && (
              <div className="flex flex-wrap gap-3 justify-between">
                {optionsRows[1].map((option) => (
                  option.next_node_id ? (
                    <motion.button
                      key={option.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={getOptionButtonClasses(options.length)}
                      onClick={() => handleOptionClick(option.next_node_id)}
                      style={{ flexBasis: `calc(${100 / optionsRows[1].length}% - ${(optionsRows[1].length - 1) * 0.75}rem)` }}
                    >
                      {option.option_text}
                    </motion.button>
                  ) : (
                    <div
                      key={option.id}
                      className="bg-gray-100 text-gray-500 py-2 px-3 rounded-xl text-left font-fun font-medium opacity-70"
                      style={{ flexBasis: `calc(${100 / optionsRows[1].length}% - ${(optionsRows[1].length - 1) * 0.75}rem)` }}
                    >
                      {option.option_text}
                      <span className="text-xs italic">(No destination set)</span>
                    </div>
                  )
                ))}
              </div>
            )}
            
            {options.length === 0 && !currentNode.is_end_node && (
              <p className="text-gray-500 italic">
                This node has no options yet. Add options in the editor to create choices.
              </p>
            )}
          </div>

          {history.length > 1 && (
            <div className="mt-6 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
                onClick={handleGoBack}
              >
                <SafeIcon icon={FiIcons.FiCornerUpLeft} className="text-lg" />
                Go Back
              </motion.button>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default StoryPreview;